import {
  adminUserSchema,
  invitationsCreatedSchema,
  invitationsRevokedSchema,
  meetingDetailSchema,
  meetingListSchema,
} from '@apovisio/contracts';
import type {
  InvitationsCreateInput,
  MeetingCreateInput,
  MeetingDetail,
  MeetingUpdate,
} from '@apovisio/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '../../lib/api';

export const meetingKeys = {
  all: ['meetings'] as const,
  list: ['meetings', 'list'] as const,
  detail: (id: string) => ['meetings', 'detail', id] as const,
};

export function useMeetings() {
  return useQuery({
    queryKey: meetingKeys.list,
    queryFn: () => apiFetch('/api/meetings', { schema: meetingListSchema }),
  });
}

export function useMeeting(id: string) {
  return useQuery({
    queryKey: meetingKeys.detail(id),
    queryFn: () => apiFetch(`/api/meetings/${id}`, { schema: meetingDetailSchema }),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch('/api/users', { schema: z.array(adminUserSchema) }),
    staleTime: 60_000,
  });
}

/** Met à jour le cache du détail avec la réponse serveur et invalide la liste. */
function useDetailMutation<TVariables>(
  id: string,
  request: (variables: TVariables) => Promise<MeetingDetail>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (detail) => {
      queryClient.setQueryData(meetingKeys.detail(id), detail);
      void queryClient.invalidateQueries({ queryKey: meetingKeys.list });
    },
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingCreateInput) =>
      apiFetch('/api/meetings', { method: 'POST', body: input, schema: meetingDetailSchema }),
    onSuccess: (detail) => {
      queryClient.setQueryData(meetingKeys.detail(detail.id), detail);
      void queryClient.invalidateQueries({ queryKey: meetingKeys.list });
    },
  });
}

export function useUpdateMeeting(id: string) {
  return useDetailMutation(id, (input: MeetingUpdate) =>
    apiFetch(`/api/meetings/${id}`, { method: 'PATCH', body: input, schema: meetingDetailSchema }),
  );
}

export function useCloseMeeting(id: string) {
  return useDetailMutation(id, () =>
    apiFetch(`/api/meetings/${id}/end`, { method: 'POST', schema: meetingDetailSchema }),
  );
}

export function useDeleteMeeting(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<undefined>(`/api/meetings/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: meetingKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: meetingKeys.list });
    },
  });
}

export function useAddCoHost(id: string) {
  return useDetailMutation(id, (userId: string) =>
    apiFetch(`/api/meetings/${id}/cohosts`, {
      method: 'POST',
      body: { userId },
      schema: meetingDetailSchema,
    }),
  );
}

export function useRemoveCoHost(id: string) {
  return useDetailMutation(id, (userId: string) =>
    apiFetch(`/api/meetings/${id}/cohosts/${userId}`, {
      method: 'DELETE',
      schema: meetingDetailSchema,
    }),
  );
}

export function useCreateInvitations(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationsCreateInput) =>
      apiFetch(`/api/meetings/${id}/invitations`, {
        method: 'POST',
        body: input,
        schema: invitationsCreatedSchema,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}

export function useRevokeInvitations(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationIds: string[]) =>
      apiFetch(`/api/meetings/${id}/invitations/revoke`, {
        method: 'POST',
        body: { invitationIds },
        schema: invitationsRevokedSchema,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}
